export interface UseCaseImage {
  src: string;
  caption?: string;
}

export interface UseCaseDetail {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  bullets: { bold?: string; text: string }[];
  images: UseCaseImage[];
  description?: string;
  externalLink?: { href: string; label: string };
}

const useCaseData: UseCaseDetail[] = [
  {
    slug: 'mrb',
    eyebrow: 'Quality & MRB',
    title: 'Automate MRB Quality Escape Modeling',
    intro:
      'Material Review Boards evaluate damaged or out-of-tolerance parts to determine whether they are acceptable, repairable, or must be scrapped. MRB analysis is critical for understanding cost, schedule, and risk implications.',
    bullets: [
      { bold: 'Scan-to-model automation:', text: 'Convert scan data directly into part-specific FEM and CFD models without manual CAD repair.' },
      { text: 'Eliminate ad-hoc mesh morphing and custom remeshing workflows.' },
      { bold: 'Reduce cost and risk:', text: 'Lower schedule impact and technical risk for high-value systems.' },
      { bold: 'Extreme damage cases:', text: 'Proven on large leading-edge defects and significant geometric deviations.' },
      { text: 'Applicable to MRB, battle damage assessment, and continuing operation decisions.' },
    ],
    images: [
      { src: '/use-cases/und_damage_1.png', caption: 'Surface data of damaged airfoil' },
      { src: '/use-cases/und_damage_morph.png', caption: 'As-damaged FEMORPH model' },
    ],
    description:
      'Even in cases with large geometric deviations or incomplete scan data, FEMORPH produces mesh-ready geometry suitable for MRB analysis.',
  },
  {
    slug: 'blend-repair',
    eyebrow: 'Repair & sustainment',
    title: 'Accelerate Design of Repairs with FEMORPH Blender',
    intro:
      "Creating part-specific repaired models can be time-consuming and complex. FEMORPH's Blender module accelerates repair design while ensuring structural and aerodynamic integrity.",
    bullets: [
      { bold: 'Rapid repair design:', text: 'Generate blend repairs quickly, saving engineering time.' },
      { bold: 'Reusable geometry library:', text: 'Apply parametric edge and surface repairs consistently across parts.' },
      { bold: 'Flexible models:', text: 'Work with nominal CAD or part-specific measured models.' },
      { bold: 'Automated workflows:', text: 'Integrate with Python scripting for optimization and efficiency.' },
    ],
    images: [
      { src: '/use-cases/blender_pbs_2.png', caption: 'GUI control of blend locations and shape' },
      { src: '/use-cases/blender_pbs_3.png', caption: 'Leading edge blend example' },
    ],
    description:
      'Even in cases with large geometric deviations or incomplete scan data, FEMORPH produces mesh-ready geometry suitable for MRB analysis.',
  },
  {
    slug: 'design',
    eyebrow: 'Design',
    title: 'Streamline 3D Design and Analysis',
    intro:
      'FEMORPH seamlessly updates finite element models to match new CAD geometries, enabling faster design iterations, optimization workflows, and rapid assessment of configuration changes.',
    bullets: [
      { bold: 'Integrated optimization:', text: 'Morph meshes to IGES or STL CAD surfaces within parametric design workflows.' },
      { bold: 'Rapid iteration:', text: 'Update models quickly to evaluate new configurations without remeshing from scratch.' },
      { bold: 'Optimization loops:', text: 'Feed FEMORPH-updated models directly into solvers to accelerate design iteration cycles.' },
      { bold: 'Flexible workflow:', text: 'Useful for uncoupled design updates when one team provides changes from another discipline.' },
      { bold: 'Faster decision-making:', text: 'Quickly determine suitability of design updates, reducing engineering turnaround time.' },
    ],
    images: [
      { src: '/use-cases/optimization_workflow.png', caption: 'Optimization of turbine disk cross section, dovetail, and bolt hole' },
    ],
    description:
      'FEMORPH can adapt to large changes in geometry. An initial seed mesh is morphed to a target CAD surface representing a large step in an optimization workflow — including a realistic dovetail, resized bolt hole, larger inner and outer diameter, and large inner bore area — while maintaining element quality throughout.',
  },
  {
    slug: 'multi',
    eyebrow: 'Multi-physics',
    title: 'Enable Integrated Multi-disciplinary Design',
    intro:
      'FEMORPH breaks down workflow silos by enabling teams across aerodynamics, thermal, structures, and lifing to work together using their own meshes. Mesh morphing allows rapid evaluation of cross-disciplinary impacts without remeshing or manual translation.',
    bullets: [
      { bold: 'Cross-discipline collaboration:', text: 'Connect CFD, thermal, and structural workflows seamlessly.' },
      { bold: 'Mesh morphing flexibility:', text: 'Work with O-grids, H-grids, polyhedral, or tetrahedral meshes directly.' },
      { bold: 'Rapid impact assessment:', text: 'See how changes in one domain affect others in minutes.' },
      { bold: 'Reduce stovepipes:', text: 'Enable integrated teams without requiring everyone to use the same software or mesh type.' },
      { bold: 'Accelerate design cycles:', text: 'Faster iterations and more informed decisions across disciplines.' },
    ],
    images: [
      { src: '/use-cases/hpt_grid.png', caption: 'CFD O- and H-grid mesh' },
      { src: '/use-cases/cooled_turbine_blade.jpg', caption: 'Heat transfer tetrahedral mesh' },
      { src: '/use-cases/pbs_sector.png', caption: 'Structural hexahedral mesh' },
    ],
    description:
      'As a CFD group modifies their aerodynamic shape defined by O-grids, H-grids, or polyhedral elements, they can see the impact on heat transfer tetrahedral models and structural hexahedral models by applying FEMORPH to each source mesh.',
  },
  {
    slug: 'ai',
    eyebrow: 'AI & data generation',
    title: 'Drive Data Generation for Artificial Intelligence',
    intro:
      'Generate large sets of physics-aware, part-specific models to define the design space. FEMORPH enables seamless creation of morphed meshes for parametric studies and AI/ML training datasets.',
    bullets: [
      { bold: 'Rapid dataset generation:', text: 'Morph source meshes to thousands of new configurations quickly.' },
      { bold: 'Parametric design coverage:', text: 'Support design space exploration using Latin Hypercube Sampling or other sampling methods.' },
      { bold: 'AI/ML ready:', text: 'Produce high-fidelity data for supervised or unsupervised learning frameworks like PyTorch and scikit-learn.' },
      { bold: 'Seamless integration:', text: "Use FEMORPH's Python interface to automate data generation workflows." },
      { bold: 'Design-informed training:', text: 'Ensure AI models are trained on geometries that respect physics and manufacturing constraints.' },
    ],
    images: [
      { src: '/use-cases/ai_workflow.png', caption: 'Random sampling the design space for ML/AI training' },
    ],
    description:
      'An LHS sample of airfoil blend geometry uses blend depth and span location as parameters. FEMORPH morphs the source mesh to each training point, producing a diverse, physics-consistent dataset ready for PyTorch, scikit-learn, or other ML frameworks.',
  },
  {
    slug: 'arbitrary',
    eyebrow: 'Design exploration',
    title: 'Explore Free-Form Design',
    intro:
      "FEMORPH's Arbitrary Morphing capability enables direct, physics-ready shape modification using control-point–defined morphing volumes. Engineers create smooth spatial deformation fields that morph existing meshes without requiring new CAD geometry.",
    bullets: [
      { bold: 'Control-point driven morphing:', text: 'Freely move 3D control points to define smooth spatial deformation fields.' },
      { bold: 'Volume or surface-based control:', text: 'Use bounding volumes or surface-overlapping control points for refined local shaping.' },
      { bold: 'CAD-free shape variation:', text: 'Generate complex geometry changes without rebuilding or parameterizing CAD models.' },
      { bold: 'Physics-ready results:', text: 'Preserve mesh quality and connectivity for immediate simulation.' },
      { bold: 'Expanded design space:', text: 'Explore non-intuitive and highly nonlinear shape variations.' },
      { bold: 'AI training enablement:', text: 'Rapidly generate large, diverse datasets without CAD workflow bottlenecks.' },
    ],
    images: [
      { src: '/use-cases/arbitrary_morph_box.png', caption: 'Airfoil with morphing control points' },
      { src: '/use-cases/arbitrary_morph_bend.png', caption: 'Tip control points used to bend airfoil' },
    ],
    description:
      'Arbitrary Morphing empowers engineers and data scientists to explore, analyze, and learn from geometry variations that would otherwise be impractical or impossible using traditional CAD-driven workflows.',
  },
  {
    slug: 'additive',
    eyebrow: 'Additive manufacturing',
    title: 'Expedite Certification of Additively Manufactured Parts',
    intro:
      'FEMORPH transforms design and inspection data into mesh-ready models for additive manufacturing. Engineers can quickly iterate designs, predict build performance, and ensure as-built fidelity for complex geometries.',
    bullets: [
      { bold: 'Fast model preparation:', text: 'Generate AM-ready meshes directly from CAD or as-measured parts.' },
      { bold: 'Complex geometry support:', text: 'Handle intricate lattice structures, conformal surfaces, and topology-optimized parts.' },
      { bold: 'Performance prediction:', text: 'Assess structural and thermal behavior before printing to reduce trial-and-error builds.' },
      { bold: 'Seamless design iteration:', text: 'Morph existing meshes to updated designs for rapid testing and optimization.' },
      { bold: 'Quality assurance:', text: 'Ensure as-built parts match design intent, reducing scrap and rework.' },
    ],
    images: [
      { src: '/use-cases/additive_workflow.png', caption: 'Manufacturing processes rely on advanced metrology systems to ensure parts meet geometric intent' },
    ],
    description:
      'A scan of an impeller manufactured using bound metal deposition (BMD) reveals ridges from the manufacturing process on both the airfoil surfaces and rim. FEMORPH morphs the nominal FEM to match the as-manufactured surface, enabling detailed assessment of additive parts at various stages in processing.',
  },
  {
    slug: 'instrument',
    eyebrow: 'Test & instrumentation',
    title: 'Improve Experimentation and Validation',
    intro:
      "Experimental data is often derived from modeling results scaled by sensor measurements. FEMORPH's part-specific models give more accurate and reliable measurement of stress, temperature, and pressure — reducing measurement uncertainty and optimizing sensor placement.",
    bullets: [
      { bold: 'Reduced measurement uncertainty:', text: 'Part-specific models provide a more accurate mapping from sensor readings to true part responses.' },
      { bold: 'Optimized sensor placement:', text: 'Improve the location of strain gages and non-contact sensors for more representative data.' },
      { bold: 'Full-field response prediction:', text: 'Capture stresses, temperatures, and pressures across the entire part, not just where sensors are located.' },
      { bold: 'Improved ratio calculations:', text: 'Update gage factors and NSMS ratios using as-manufactured models rather than nominal CAD.' },
      { bold: 'Confidence in high-performance parts:', text: 'Reduce uncertainty to enable design and validation of advanced, high-performance components.' },
    ],
    images: [
      { src: '/use-cases/pbs4_gages.png', caption: 'Model of an airfoil with strain gage locations' },
      { src: '/use-cases/pbs4_gage_variation.png', caption: 'Large variation of part-specific gage measurements' },
    ],
    description:
      'Research on the PBS 4 experimental rotor found significant part-specific variation in gage limits — some blades required reduced operating limits for safety while others could operate further into the operating range. Understanding part-specific behavior enables these informed decisions.',
    externalLink: {
      href: 'https://etd.ohiolink.edu/acprod/odb_etd/etd/r/1501/10?clear=10&p10_accession_num=wright1557245676336916',
      label: 'Visit dissertation for more',
    },
  },
  {
    slug: 'validation',
    eyebrow: 'Research',
    title: 'Validate Part-Specific Modeling',
    intro:
      'FEMORPH is validated through rigorous, physics-based experimental programs led by engineers and researchers. By integrating advanced metrology and structural dynamics testing, FEMORPH as-manufactured models are proven against some of the most demanding validation cases in structural mechanics.',
    bullets: [
      { bold: 'Engineer-led validation:', text: 'Developed and validated by researchers actively advancing metrology and experimental mechanics.' },
      { bold: 'Integrated validation pipeline:', text: 'Structured light scanning, model morphing, and experimental testing combined into a single workflow.' },
      { bold: 'Improved measurement fidelity:', text: 'Continuous refinement of structured light scanning accuracy for high-confidence geometry capture.' },
      { bold: 'Advanced vibration testing:', text: 'Specialized methods developed to measure mistuned dynamics in bladed rotors.' },
      { bold: 'True mistuning physics:', text: 'Captures blade-to-blade variability, modal coupling, and nonlinear sensitivity to micron-level geometry changes.' },
      { bold: 'Proven decision confidence:', text: 'Experimental correlation demonstrates FEMORPH models can be trusted for critical engineering decisions.' },
      { bold: 'Fielded and trusted:', text: 'Validated technology relied upon by industry and the United States Air Force.' },
    ],
    images: [
      { src: '/use-cases/exp-full-mode-192.png', caption: 'Experimentally measured mistuned mode shape' },
      { src: '/use-cases/gmm-full-mode-192.png', caption: 'FEMORPH as-manufactured predicted mistuned mode shape' },
    ],
    description:
      'A 2025 validation study on the AFRL PBS Stage 5 Research Rotor established scan-to-scan measurement error below 0.0003 in. The morphed as-manufactured model was compared to traveling wave excitation data collected with a Polytec laser vibrometer. The high-frequency mistuned mode shape — showing archetypical mode localization — is accurately captured by the FEMORPH model, giving customers the confidence to apply the software to failure-critical parts.',
  },
  {
    slug: 'industry40',
    eyebrow: 'Digital manufacturing',
    title: 'Empower Industry 4.0',
    intro:
      'Transform inspection data into actionable, physics-based insight. FEMORPH generates high-fidelity digital twins from as-measured parts, closing the gap between inspection, simulation, and quality decisions.',
    bullets: [
      { bold: 'High-fidelity digital twins:', text: 'Morph finite element models to match real, manufactured components.' },
      { bold: 'Performance-based evaluation:', text: 'Move beyond tolerance checks to functional performance predictions.' },
      { bold: 'Automated workflows:', text: 'Generate, analyze, and link as-built models to acceptance criteria at scale.' },
      { bold: 'Predictive quality:', text: 'Reduce scrap, accelerate disposition, and make smarter shop-floor decisions.' },
      { bold: 'Integrated manufacturing intelligence:', text: 'Combine metrology, simulation, and automation in a single workflow.' },
    ],
    images: [
      { src: '/use-cases/industry40_gpt3.png', caption: 'Industry 4.0 digital manufacturing workflow' },
    ],
  },
  {
    slug: 'digital-twin',
    eyebrow: 'Digital twin',
    title: 'Deliver Instance-Level Digital Twins',
    intro:
      'FEMORPH transforms inspection data into physics-capable digital twins, bridging the gap between nominal CAD and as-built components. Engineers can predict, validate, and analyze real-world performance at the individual part level.',
    bullets: [
      { bold: 'Realistic digital twins:', text: 'Morph FE models to match as-measured geometry for accurate simulations.' },
      { bold: 'Traceable digital thread:', text: 'Keep inspection, simulation, and acceptance data connected across the lifecycle.' },
      { bold: 'Performance validation:', text: 'Evaluate structural and functional behavior at the part level.' },
      { bold: 'Lifecycle insights:', text: 'Support predictive maintenance, design validation, and operational decision-making.' },
      { bold: 'Engineering-ready data:', text: 'Inspection results feed directly into simulation-driven assessments.' },
    ],
    images: [
      { src: '/use-cases/digital_twin_gpt2.png', caption: 'Instance-level digital twin and thread' },
    ],
  },
];

export default useCaseData;
